/*
 * HomeCrest CUSTOMISE panel
 * ===========================================================================
 * Supplies the listener the Customise CTA never had. `homecrest-pdp.js`
 * already dispatched `homecrest:action` on click; this opens the panel when
 * that event carries action "customise", and ignores every other action so
 * Create Your Own and Talk to an Expert keep their own behaviour.
 *
 * Choosing an option here does NOT reimplement variant selection. It clicks
 * the matching input inside Horizon's own <variant-picker>, so Horizon runs
 * its normal flow - price, SKU, gallery, availability, add-to-cart target -
 * and the panel can never disagree with the page behind it.
 *
 * The panel is a native <dialog>: focus trapping, Escape and the top layer all
 * come from the browser, and nothing in the page layout is disturbed.
 */

const ACTION_EVENT = 'homecrest:action';
const PRODUCT_SELECT = 'shopify:product:select';

/**
 * The Customise panel belonging to a trigger.
 *
 * Section first, then the document. Two triggers open this one panel and they
 * do NOT live together: the purchase column's Customise card sits in the
 * product-information section alongside the dialog, while the Make it yours
 * section lower down the page is a separate section. Scoping to the trigger's
 * own section alone would leave that second card inert; the document fallback
 * lets both reach the single panel, and the section lookup still wins first so
 * a page carrying two panels would open the right one.
 *
 * @returns {HTMLDialogElement | null}
 */
function dialogFor(trigger) {
  const scope = trigger?.closest('.shopify-section');
  return (
    scope?.querySelector('[data-homecrest-customise-dialog]') ??
    document.querySelector('[data-homecrest-customise-dialog]')
  );
}

/** The PDP's real variant picker, which stays the single source of truth. */
function pickerFor(dialog) {
  const scope = dialog.closest('.shopify-section') ?? document;
  return scope.querySelector('variant-picker');
}

/**
 * Mirror the picker's current state onto the panel, so the panel reflects
 * changes made either here or directly on the PDP.
 */
function syncFromPicker(dialog) {
  const picker = pickerFor(dialog);
  if (!picker) return;

  const checked = [...picker.querySelectorAll('.variant-option input:checked')];

  for (const button of dialog.querySelectorAll('[data-hc-cust-value]')) {
    const name = button.dataset.optionName;
    const value = button.dataset.optionValue;
    const match = checked.find((input) => input.dataset.optionName === name);
    button.setAttribute('aria-pressed', String(match?.value === value));

    // Availability is Horizon's call, not ours — read it back rather than
    // computing a second opinion about which combinations exist.
    const input = [...picker.querySelectorAll('.variant-option input')].find(
      (i) => i.dataset.optionName === name && i.value === value
    );
    if (input?.getAttribute('aria-disabled') === 'true') {
      button.setAttribute('data-unavailable', 'true');
    } else {
      button.removeAttribute('data-unavailable');
    }
  }

  const summary = dialog.querySelector('[data-hc-cust-summary]');
  if (summary) summary.textContent = checked.map((i) => i.value).join(' / ');
}

function openDialog(dialog) {
  if (!dialog || dialog.open) return;
  syncFromPicker(dialog);
  dialog.showModal();
}

function bind() {
  if (document.documentElement.dataset.hcCustomiseBound === 'true') return;
  document.documentElement.dataset.hcCustomiseBound = 'true';

  // 1. Open on the Customise action only.
  document.addEventListener(ACTION_EVENT, (event) => {
    if (event.detail?.action !== 'customise') return;
    openDialog(dialogFor(event.detail.trigger));
  });

  // 2. Panel interactions.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const dialog = target.closest('[data-homecrest-customise-dialog]');
    if (!dialog) return;

    if (target.closest('[data-hc-cust-close]')) {
      dialog.close();
      return;
    }

    const value = target.closest('[data-hc-cust-value]');
    if (value) {
      if (value.getAttribute('data-unavailable') === 'true') return;

      const picker = pickerFor(dialog);
      const input = picker
        ? [...picker.querySelectorAll('.variant-option input')].find(
            (i) => i.dataset.optionName === value.dataset.optionName && i.value === value.dataset.optionValue
          )
        : null;

      // Hand straight back to Horizon. If the input is missing the panel does
      // nothing rather than guessing at a variant id.
      if (input && !input.checked) input.click();
      return;
    }

    const submit = target.closest('[data-hc-cust-submit]');
    if (submit) {
      const request = dialog.querySelector('[data-hc-cust-request]');
      const picker = pickerFor(dialog);
      const variantId = picker?.closest('.shopify-section')?.querySelector('input[name="id"]')?.value;

      // The AI layer listens for this. Nothing is sent from here, because
      // there is no generation system yet to send it to.
      document.dispatchEvent(
        new CustomEvent('homecrest:customise:submit', {
          bubbles: true,
          detail: {
            endpoint: submit.dataset.endpoint || undefined,
            productHandle: dialog.dataset.productHandle,
            productId: dialog.dataset.productId,
            variantId,
            selectedOptions: [...(picker?.querySelectorAll('.variant-option input:checked') ?? [])].map((i) => ({
              name: i.dataset.optionName,
              value: i.value,
            })),
            request: request instanceof HTMLTextAreaElement ? request.value.trim() : '',
          },
        })
      );
    }
  });

  // 3. Keep the panel in step when the variant changes on the PDP itself.
  document.addEventListener(PRODUCT_SELECT, (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    for (const dialog of document.querySelectorAll('[data-homecrest-customise-dialog]')) {
      // Re-sync after Horizon has finished morphing the picker.
      Promise.resolve(event.promise)
        .then(() => syncFromPicker(dialog))
        .catch(() => {});
    }
  });
}

bind();
